import { useTranslation } from "react-i18next";
import styles from "./newStyles.module.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import classNames from "classnames";
import IconButton from "./IconButton";
import Search from "./Search";
import Dropdown from "./DropDown";
import OrganizationSelect from "./OrganizationSelect";
import {
  active_status,
  cashInOptions,
  closed_status,
  categoryOptions,
  operationalStatusOptions,
  operationalTypeOptions,
  shippingCompanyOptions,
  cashOutOptions,
  escalationOptions,
  operationResultOptions,
  cashOutOutTypeOptions,
} from "./constants";
import {
  AddOnFeeCategory,
  AddOnFees,
  EContractType,
  EFeePayee,
  ESenderRole,
  FeeCalculationType,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import {
  getContracts,
  getContractDateFields,
} from "../../../setup/api/services/contract";
import type { DateFieldOption } from "./IconButton";
import Table, { TableColumn } from "./Table";
import Spinner from "../../../components/common/Spinner";
import Link from "./Table/components/Link";
import { getMappedContractType } from "../../../setup/util/helpers";
import StatusPill from "./Table/components/StatusPill";
import Money from "./Table/components/Money";
import UserInfo from "./Table/components/UserInfo";
import Pagination from "../../../components/pagination";

type ColumnKey =
  | "name"
  | "id"
  | "type"
  | "createdAt"
  | "status"
  | "organization"
  | "paid"
  | "cashInMethod"
  | "cashOut"
  | "cashOutMethod"
  | "cashOutType"
  | "fees"
  | "buyer"
  | "seller"
  | "category"
  | "operationalStatus"
  | "shippingCompany"
  | "operationalType"
  | "escalation"
  | "operationResult"
  | "lockerShipmentPaymentStatus"
  | "lockerPaymentCapability";

type MenuOption = {
  value: ColumnKey;
  label: string;
};

type ColumnVisibilityState = Record<ColumnKey, boolean>;

const initialColumnVisibility: ColumnVisibilityState = {
  name: true,
  id: true,
  type: true,
  createdAt: true,
  status: true,
  organization: true,
  paid: true,
  cashInMethod: true,
  cashOut: true,
  cashOutMethod: true,
  cashOutType: true,
  fees: true,
  buyer: true,
  seller: true,
  category: true,
  operationalStatus: true,
  shippingCompany: true,
  operationalType: true,
  escalation: true,
  operationResult: true,
  lockerShipmentPaymentStatus: false,
  lockerPaymentCapability: false,
};

const pageSize = 10;

const NewList = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [contracts, setContracts] = useState<IContractDetailsResponseData[]>(
    []
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState<string>("ALL_CASES");
  const [cashIn, setCashIn] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [operationalStatus, setOperationalStatus] = useState<string>("");
  const [operationalType, setOperationalType] = useState<string>("");
  const [shippingCompany, setShippingCompany] = useState<string>("");
  const [cashOutType, setCashOutType] = useState<string>("");
  const [escalation, setEscalation] = useState<string>("");
  const [operationResult, setOperationResult] = useState<string>("");
  const [cashOutOutType, setCashOutOutType] = useState<string>("");
  const [organization, setOrganization] = useState<{
    value: string;
    label: string;
  }>({ value: "", label: "الكل" });
  const [searchBy, setSearchBy] = useState("contractId");
  const [dateFilterCol, setDateFilterCol] = useState("createdAt");
  const [dateFieldOptions, setDateFieldOptions] = useState<DateFieldOption[]>(
    []
  );

  const [lockerPaymentStatus, setLockerPaymentStatus] = useState<string>("");
  const [redboxPendingCount, setRedboxPendingCount] = useState<number>(0);

  const tableData = useMemo(() => {
    return contracts.map((contract) => {
      let currentCashIn = contract.itemPrice;
      const currentBuyer =
        contract.customer || contract.partiesProfiles?.CUSTOMER?.[0];
      const currentSeller =
        contract.provider || contract.partiesProfiles?.PROVIDER?.[0];

      if (contract.type === EContractType.MILESTONE_CONTRACT) {
        let totalAmount = 0;

        const customers = contract.parties?.filter(
          (p) => p.role === ESenderRole.CUSTOMER
        );

        customers?.forEach((customer) => {
          let addOnFees = 0;
          const baseAmount = customer?.amount || 0;

          const currentCustomerAddOnFees = contract?.addonFeesList?.filter(
            (fee: AddOnFees) =>
              fee?.payee === customer.phoneNumber &&
              ((contract.feePayee === EFeePayee.CUSTOMER &&
                fee.type !== AddOnFeeCategory.SERVICE_FEES) ||
                contract.feePayee !== EFeePayee.CUSTOMER)
          );

          currentCustomerAddOnFees?.forEach((addOnFee) => {
            if (addOnFee.feeType === FeeCalculationType.FLAT) {
              addOnFees += addOnFee.amount;
            } else {
              addOnFees += (baseAmount * addOnFee.amount) / 100;
            }
          });

          totalAmount += baseAmount + addOnFees;
        });

        currentCashIn = totalAmount;

        const paidHarajTransaction = (contract?.transactions as any[])?.find(
          (transaction) =>
            (transaction?.type === "CUSTOMER_PAY" ||
              transaction?.type === "MANUAL_BANK_TRANSFER") &&
            transaction?.status === "SUCCESS"
        );

        if (paidHarajTransaction) {
          currentCashIn = paidHarajTransaction.amount;
        }
      }

      return {
        id: contract.shortId,
        name: contract.itemDetail.title,
        type: getMappedContractType(contract.type),
        createdAt: new Date(contract.createdAt).toLocaleDateString("ar-EG", {
          year: "numeric",
          month: "long",
          day: "2-digit",
        }),
        status: contract.status,
        organization: contract.organizationCode,
        paid: currentCashIn,
        cashInMethod: contract.operationsMetaData?.cashIn,
        cashOut: (currentCashIn - (contract.fee || 0)).toFixed(2),
        cashOutMethod: contract.operationsMetaData?.cashOut,
        cashOutType: contract.operationsMetaData?.cashOutOutType,
        fees: contract.fee || 0,
        buyer: currentBuyer?.profile?.fullName,
        buyerProfile: currentBuyer,
        seller: currentSeller?.profile?.fullName,
        sellerProfile: currentSeller,
        category: contract.operationsMetaData?.category,
        operationalStatus: contract.operationsMetaData?.operationStatus,
        shippingCompany: contract.operationsMetaData?.shippingCompany,
        operationalType: contract.operationsMetaData?.operationalType,
        escalation: contract.operationsMetaData?.escalation,
        operationResult: contract.operationsMetaData?.operationResult,
        lockerShipmentPaymentStatus: contract.shipment?.isLocker
          ? contract.shipment?.isLockerShipmentPaid ?? false
          : null,
        lockerPaymentCapability: contract.shipment?.lockerPaymentUrl ?? null,
      };
    });
  }, [contracts]);

  const mainMenuOptions: MenuOption[] = [
    { value: "name", label: t("lbl_milestone") },
    { value: "id", label: t("code") },
    { value: "type", label: t("lbl_contract_type") },
    { value: "createdAt", label: t("lbl_created_at") },
    { value: "status", label: t("lbl_status") },
    { value: "organization", label: t("lbl_organization") },
    { value: "paid", label: t("lbl_payment") },
    { value: "cashOut", label: t("lbl_transfer") },
    { value: "fees", label: t("lbl_more_details_sheet_fees") },
    { value: "buyer", label: t("lbl_buyer") },
    { value: "seller", label: t("lbl_the_seller") },
  ];

  const operationMenuOptions: MenuOption[] = [
    { value: "cashInMethod", label: t("lbl_payment_method") },
    { value: "cashOutMethod", label: t("lbl_cash_out_type") },
    { value: "cashOutType", label: t("lbl_cash_out_out_type") },
    { value: "category", label: t("lbl_category") },
    { value: "operationalStatus", label: t("lbl_operational_status") },
    { value: "shippingCompany", label: t("lbl_shipping_company") },
    { value: "operationalType", label: t("lbl_operational_type") },
    { value: "escalation", label: t("lbl_escalation") },
    { value: "operationResult", label: t("lbl_operation_result") },
  ];

  const searchOptions = [
    { value: "contractId", label: t("lbl_contract_code") },
    { value: "userId", label: t("lbl_user_code") },
    { value: "contractName", label: t("TRANSACTION_TITLE") },
  ];

  const columns: TableColumn[] = [
    {
      key: "name",
      label: t("lbl_milestone"),
      width: "150px",
      render: (value, row) => (
        <Link
          text={value}
          contractId={row.id} // 👈 comes from tableData
        />
      ),
    },
    { key: "id", label: t("code"), width: "200px" },
    { key: "type", label: t("lbl_contract_type"), width: "150px" },
    { key: "createdAt", label: t("lbl_created_at"), width: "150px" },
    {
      key: "status",
      label: t("lbl_status"),
      width: "150px",
      render: (value) => <StatusPill status={value} />,
    },
    {
      key: "lockerShipmentPaymentStatus",
      label: t("lbl_locker_payment_status_column"),
      width: "150px",
      render: (value: boolean | null) => {
        if (value === null || value === undefined) return <div>-</div>;
        const isPaid = value === true;
        return (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}>
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: isPaid ? "#00897b" : "#f59e0b",
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: "14px", color: "#2c333c" }}>
              {isPaid ? t("lbl_locker_paid") : t("lbl_locker_not_paid")}
            </span>
          </div>
        );
      },
    },
    {
      key: "lockerPaymentCapability",
      label: t("lbl_locker_payment_capability_column"),
      width: "150px",
      render: (value: string | null) => {
        const isPayable = !!value;
        return (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}>
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: isPayable ? "#00897b" : "#f59e0b",
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: "14px", color: "#2c333c" }}>
              {isPayable
                ? t("lbl_locker_payable")
                : t("lbl_locker_not_payable")}
            </span>
          </div>
        );
      },
    },
    { key: "organization", label: t("lbl_organization"), width: "150px" },
    {
      key: "paid",
      label: t("lbl_payment"),
      width: "150px",
      render: (value) => <Money amount={value} />,
    },
    { key: "cashInMethod", label: t("lbl_payment_method"), width: "150px" },
    {
      key: "cashOut",
      label: t("lbl_transfer"),
      width: "150px",
      render: (value) => <Money amount={value} />,
    },
    { key: "cashOutMethod", label: t("lbl_cash_out_type"), width: "150px" },
    { key: "cashOutType", label: t("lbl_cash_out_out_type"), width: "150px" },
    {
      key: "fees",
      label: t("lbl_more_details_sheet_fees"),
      width: "150px",
      render: (value) => <Money amount={value} />,
    },
    {
      key: "buyer",
      label: t("lbl_buyer"),
      width: "150px",
      render: (value, row) => (
        <UserInfo
          name={value}
          isNationalIdVerified={row.buyerProfile?.nationalIdVerified || false}
          image={row.buyerProfile?.profile?.photoUrl || ""}
        />
      ),
    },
    {
      key: "seller",
      label: t("lbl_the_seller"),
      width: "150px",
      render: (value, row) => (
        <UserInfo
          name={value}
          isNationalIdVerified={
            row.sellerProfile?.isNationalIdVerified || false
          }
          image={row.sellerProfile?.profile?.photoUrl || ""}
        />
      ),
    },
    { key: "category", label: t("lbl_category"), width: "150px" },
    {
      key: "operationalStatus",
      label: t("lbl_operational_status"),
      width: "150px",
    },
    {
      key: "shippingCompany",
      label: t("lbl_shipping_company"),
      width: "150px",
    },
    {
      key: "operationalType",
      label: t("lbl_operational_type"),
      width: "150px",
    },
    { key: "escalation", label: t("lbl_escalation"), width: "150px" },
    {
      key: "operationResult",
      label: t("lbl_operation_result"),
      width: "150px",
    },
  ];

  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>(initialColumnVisibility);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const onToggleFilters = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleMenuChange = (value: string, checked: boolean) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [value]: checked,
    }));
  };

  const handleSelectAllMain = (checked: boolean) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev };

      mainMenuOptions.forEach((opt) => {
        updated[opt.value] = checked;
      });

      return updated;
    });
  };

  const handleSelectAllOperations = (checked: boolean) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev };

      operationMenuOptions.forEach((opt) => {
        updated[opt.value] = checked;
      });

      return updated;
    });
  };

  const lockerPaymentOptions = useMemo(
    () => [
      { id: "", label: t("lbl_all") },
      { id: "NOT_PAID", label: t("lbl_locker_not_paid") },
      { id: "PAID", label: t("lbl_locker_paid") },
    ],
    [t]
  );

  const list = useMemo(() => {
    return [
      { id: 0, title: t("lbl_home_active_transactions_title") },
      { id: 1, title: t("lbl_previous_transactions") },
      { id: 2, title: t("lbl_transfer_requests") },
      { id: 3, title: t("lbl_redbox_payment_tab"), count: redboxPendingCount },
    ];
  }, [t, redboxPendingCount]);

  const effectiveColumnVisibility = useMemo(
    () => ({
      ...columnVisibility,
      lockerShipmentPaymentStatus: selectedIndex === 3,
      lockerPaymentCapability: selectedIndex === 3,
    }),
    [columnVisibility, selectedIndex]
  );

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const isLockerShipmentPaid =
        selectedIndex === 3 && lockerPaymentStatus === "PAID"
          ? true
          : selectedIndex === 3 && lockerPaymentStatus === "NOT_PAID"
          ? false
          : null;
      const body = {
        listType:
          selectedIndex === 0
            ? "active"
            : selectedIndex === 1
            ? "previous"
            : selectedIndex === 2
            ? "transfer"
            : "redbox",
        status: String(status),
        participantId: searchBy === "userId" ? searchValue : "",
        contractTitle: searchBy === "contractName" ? searchValue : "",
        contractId: searchBy === "contractId" ? searchValue : "",
        dateFilterCol: dateFilterCol,
        dateFilterFrom: dateRange.startDate
          ? moment(dateRange.startDate).format("YYYY-MM-DD HH:mm")
          : "",
        dateFilterTo: dateRange.endDate
          ? moment(dateRange.endDate).endOf("day").format("YYYY-MM-DD HH:mm")
          : "",
        page: page - 1,
        size: pageSize,
        organizationCode: organization.value || "",
        isFetchMilestone: true,
        cashIn: String(cashIn),
        category: String(category),
        operationalStatus: String(operationalStatus),
        operationalType: String(operationalType),
        escalation: String(escalation),
        operationResult: String(operationResult),
        shippingCompany: String(shippingCompany),
        cashOut: String(cashOutOutType),
        cashOutOutType: String(cashOutOutType),
        ...(selectedIndex === 3 && { isLocker: true }),
        ...(selectedIndex === 3 &&
          isLockerShipmentPaid !== null && { isLockerShipmentPaid }),
      };
      const response = await getContracts(body);
      setContracts(response.data.content);
      setPagesCount(response.data.totalPages);
      setTotalCount(response.data.totalElements);
      if (selectedIndex === 3) {
        setRedboxPendingCount(response.data.totalElements);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedIndex,
    status,
    dateFilterCol,
    dateRange.endDate,
    dateRange.startDate,
    page,
    organization.value,
    cashIn,
    category,
    operationalStatus,
    operationalType,
    escalation,
    operationResult,
    shippingCompany,
    cashOutOutType,
    searchValue,
    lockerPaymentStatus,
  ]);

  const fetchRedboxPendingCount = useCallback(async () => {
    try {
      const response = await getContracts({
        listType: "redbox",
        isLocker: true,
        isFetchMilestone: true,
        page: 0,
        size: 1,
      });
      setRedboxPendingCount(response.data.totalElements || 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  useEffect(() => {
    getContractDateFields()
      .then((response) => {
        const options: DateFieldOption[] = response.data.map(
          (opt: { value: string; label: { ar: string; en: string } }) => ({
            value: opt.value,
            label: opt.label.ar,
          })
        );
        setDateFieldOptions(options);
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchRedboxPendingCount();
  }, [fetchRedboxPendingCount]);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t("lbl_transactions")}</h1>
      <div className={styles.tabContainer}>
        {list?.map((listItem) => (
          <li
            onClick={() => {
              setSelectedIndex(listItem?.id);
              setPage(1);
              setLockerPaymentStatus("");
            }}
            key={listItem?.title}
            className={classNames(
              styles.listItem,
              {
                [styles.listItemSelected]: listItem?.id === selectedIndex,
              },
              { [styles.firstItem]: listItem?.id === 0 },
              {
                [styles.lastItem]:
                  listItem?.id === list?.[list?.length - 1]?.id,
              }
            )}>
            {listItem?.title}
            {"count" in listItem && (
              <span
                style={{
                  background: "#fff1c9",
                  borderRadius: "12px",
                  minWidth: "20px",
                  height: "20px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#000",
                  padding: "0 5px",
                  marginInlineStart: "6px",
                }}>
                {listItem.count ?? 0}
              </span>
            )}
          </li>
        ))}
      </div>
      <div className={styles.filtersContainer}>
        <div className={styles.rightSection}>
          <IconButton
            mode="menu"
            icon="/assets/images/svg/settings.svg"
            mainOptions={mainMenuOptions}
            operationOptions={operationMenuOptions}
            selectedValues={columnVisibility}
            onChange={handleMenuChange}
            onSelectAllMain={handleSelectAllMain}
            onSelectAllOperations={handleSelectAllOperations}
          />
          <IconButton
            icon="/assets/images/svg/filter.svg"
            mode="toggle"
            onToggle={onToggleFilters}
            isActive={isFilterOpen}
          />
          <IconButton
            icon="/assets/images/svg/calendar.svg"
            mode="calendar"
            dateRange={dateRange}
            onDateChange={setDateRange}
            dateFieldOptions={dateFieldOptions}
            dateFieldValue={dateFilterCol}
            onDateFieldChange={(value) => {
              setDateFilterCol(value);
              setPage(1);
            }}
          />
          <Search
            value={searchValue}
            onChange={handleSearchChange}
            searchBy={searchBy}
            onSearchByChange={setSearchBy}
            searchOptions={searchOptions}
            className={styles.searchInputCustom}
          />
        </div>
        <div className={styles.leftSection}>
          {selectedIndex === 3 && (
            <Dropdown
              options={lockerPaymentOptions}
              label={t("lbl_locker_payment_status_filter")}
              onChange={(value) => {
                setLockerPaymentStatus(value);
                setPage(1);
              }}
              value={lockerPaymentStatus}
            />
          )}
          {selectedIndex !== 2 && selectedIndex !== 3 && (
            <Dropdown
              options={selectedIndex === 0 ? active_status : closed_status}
              label={t("lbl_contract_details_transaction_status")}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              value={status}
            />
          )}
          <OrganizationSelect
            value={organization}
            onChange={(newValue) => {
              if (!newValue) {
                setOrganization({ value: "", label: "الكل" });
                return;
              }
              setOrganization(newValue as { value: string; label: string });
              setPage(1);
            }}
            t={t}
          />
        </div>
      </div>
      {isFilterOpen && (
        <div className={styles.advancedFiltersContainer}>
          <Dropdown
            options={categoryOptions}
            label={t("lbl_category")}
            onChange={(value) => {
              setCategory(value);
              setPage(1);
            }}
            value={category}
          />
          <Dropdown
            options={operationalTypeOptions}
            label={t("lbl_operational_type")}
            onChange={(value) => {
              setOperationalType(value);
              setPage(1);
            }}
            value={operationalType}
          />
          <Dropdown
            options={operationalStatusOptions}
            label={t("lbl_operational_status")}
            onChange={(value) => {
              setOperationalStatus(value);
              setPage(1);
            }}
            value={operationalStatus}
          />
          <Dropdown
            options={cashInOptions}
            label={t("lbl_payment_method")}
            onChange={(value) => {
              setCashIn(value);
              setPage(1);
            }}
            value={cashIn}
          />
          <Dropdown
            options={escalationOptions}
            label={t("lbl_escalation")}
            onChange={(value) => {
              setEscalation(value);
              setPage(1);
            }}
            value={escalation}
          />
          <Dropdown
            options={shippingCompanyOptions}
            label={t("lbl_shipping_company")}
            onChange={(value) => {
              setShippingCompany(value);
              setPage(1);
            }}
            value={shippingCompany}
          />
          <Dropdown
            options={cashOutOptions}
            label={t("lbl_cash_out_type")}
            onChange={(value) => {
              setCashOutType(value);
              setPage(1);
            }}
            value={cashOutType}
          />
          <Dropdown
            options={cashOutOutTypeOptions}
            label={t("lbl_cash_out_out_type")}
            onChange={(value) => {
              setCashOutOutType(value);
              setPage(1);
            }}
            value={cashOutOutType}
          />
          <Dropdown
            options={operationResultOptions}
            label={t("lbl_operation_result")}
            onChange={(value) => {
              setOperationResult(value);
              setPage(1);
            }}
            value={operationResult}
          />
        </div>
      )}
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Spinner />
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            columnVisibility={effectiveColumnVisibility}
            data={tableData}
          />
          <div className={styles.paginationContainer}>
            <Pagination
              countName={t("balance_transaction_title")}
              pageSize={pageSize}
              pagesCount={pagesCount}
              page={page}
              length={totalCount}
              handlePaginationChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default NewList;
